import { useState } from "react";

import { FormulaMemberSchema, FormulaSchema } from "../../model/formula";

interface FormulaFieldPaletteProps {
  schema: FormulaSchema;
  onInsert: (path: string) => void;
}

interface NodeProps {
  schema: FormulaSchema;
  label: string;
  path: string;
  typeRef?: string | null;
  isPrimitive: boolean;
  displayType?: string;
  onInsert: (path: string) => void;
  ancestors: Set<string>;
  depth: number;
}

const MAX_DEPTH = 6;

const SchemaNode = (props: NodeProps) => {
  const [expanded, setExpanded] = useState(false);

  const type = props.typeRef ? props.schema.types[props.typeRef] : undefined;
  const isExpandable =
    !props.isPrimitive &&
    !!type &&
    props.depth < MAX_DEPTH &&
    !!props.typeRef &&
    !props.ancestors.has(props.typeRef);

  return (
    <li className="formula-palette__node">
      <div className="formula-palette__row">
        {isExpandable ? (
          <button
            type="button"
            className="formula-palette__toggle"
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? "▾" : "▸"}
          </button>
        ) : (
          <span className="formula-palette__toggle formula-palette__toggle--leaf" />
        )}
        <button
          type="button"
          className="formula-palette__label"
          title={`Insert ${props.path}`}
          onClick={() => props.onInsert(props.path)}
        >
          {props.label}
        </button>
        {props.displayType && <span className="formula-palette__type">{props.displayType}</span>}
      </div>
      {isExpandable && expanded && type && (
        <ul className="formula-palette__children">
          {type.members.map((member: FormulaMemberSchema) => (
            <SchemaNode
              key={member.name}
              schema={props.schema}
              label={member.name}
              path={`${props.path}.${member.name}`}
              typeRef={member.typeRef}
              isPrimitive={member.isPrimitive}
              displayType={member.displayType}
              onInsert={props.onInsert}
              ancestors={new Set(props.ancestors).add(props.typeRef!)}
              depth={props.depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

/**
 * A compact, browsable tree of the root objects and their members. Clicking a field name inserts
 * its full path (e.g. `TransactionInfo.Transaction.TypeName`) at the editor cursor.
 */
export const FormulaFieldPalette = (props: FormulaFieldPaletteProps) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="formula-palette">
      <button
        type="button"
        className="formula-palette__header"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? "▾" : "▸"} Available fields
      </button>
      {open && (
        <ul className="formula-palette__root">
          {props.schema.rootObjects.map((root) => (
            <SchemaNode
              key={root.name}
              schema={props.schema}
              label={root.name}
              path={root.name}
              typeRef={root.typeRef}
              isPrimitive={root.isPrimitive}
              displayType={root.displayType}
              onInsert={props.onInsert}
              ancestors={new Set<string>()}
              depth={0}
            />
          ))}
        </ul>
      )}
    </div>
  );
};
